import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Tag } from "@/components/Reveal";
import { ParallaxImage } from "@/components/ParallaxImage";
import { IMG } from "@/data/images";

const LAYERS = [
  {
    id: "coating",
    n: "01",
    title: "Bio-Protective Coating",
    image: IMG.labGlass,
    alt: "Laboratory glassware used in bio-formulation research",
    desc: "A micro-thin, plant-derived coating in development, intended for application as a wash or spray. Its goal is to slow the two processes that age produce fastest: respiration and moisture loss.",
    specs: ["Designed to be edible, tasteless and odourless", "Goal: rinses clean, with no harsh residues", "Formulated from plant-derived inputs", "Designed for post-harvest application in packhouse lines"],
  },
  {
    id: "breathability",
    n: "02",
    title: "Breathability Matrix",
    image: IMG.verticalFarm,
    alt: "Greens growing in a controlled vertical farming environment",
    desc: "Produce keeps breathing after harvest. Our coating and packaging concepts are designed to balance oxygen and carbon dioxide around each product — with the aim of slowing ripening without suffocating the tissue.",
    specs: ["Gas exchange designed per produce type", "Designed to slow ethylene-driven ripening", "Targets reduced condensation and surface mould risk", "In trials across leafy greens, tomatoes and soft fruit"],
  },
  {
    id: "antimicrobial",
    n: "03",
    title: "Natural Antimicrobials",
    image: IMG.labMicroscope,
    alt: "Scientist studying samples through a microscope",
    desc: "Spoilage is largely microbial. Our R&D programme is evaluating plant-extract compounds against the spoilage organisms most common in regional supply chains.",
    specs: ["Targets spoilage bacteria and moulds", "No synthetic preservatives in target formulation", "Being screened against regional spoilage strains", "Safety evaluations planned in line with SFDA frameworks"],
  },
  {
    id: "intelligence",
    n: "04",
    title: "Cold-Chain Intelligence",
    image: IMG.farmTech,
    alt: "Modern agricultural technology in a field",
    desc: "Protection is only half the story. A planned sensor layer across transport and storage will track the conditions that decide shelf life — and flag risks before freshness is lost.",
    specs: ["Temperature and humidity telemetry", "Early-warning alert concepts across the chain", "Journey data to feed continuous R&D", "Planned dashboards for operations teams"],
  },
];

const APPROACH = [
  { n: "01", title: "Discover", text: "Screen plant-derived compounds and materials for protective performance." },
  { n: "02", title: "Formulate", text: "Engineer produce-specific formulations and packaging systems." },
  { n: "03", title: "Validate", text: "Controlled shelf-life trials, then independent verification." },
  { n: "04", title: "Scale", text: "Pilot with commercial partners across the Kingdom, then the region." },
];

export default function Technology() {
  const [active, setActive] = useState(LAYERS[0]);

  return (
    <div data-testid="technology-page">
      <PageHero
        chapter="04"
        tag="Technology"
        testId="technology-hero"
        lines={[<>The science of</>, <>staying fresh.</>]}
        lead="Four layers of protection in development, designed to work together: a plant-derived coating, a tuned atmosphere, natural antimicrobial defence and data across the cold chain."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24 sm:pb-32">
        <Reveal><Tag>Explore the layers</Tag></Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2">
            {LAYERS.map((l) => (
              <button
                key={l.id}
                data-testid={`tech-layer-tab-${l.id}`}
                onClick={() => setActive(l)}
                className={`text-left shrink-0 lg:shrink border px-6 py-5 transition-colors min-w-[220px] lg:min-w-0 ${
                  active.id === l.id
                    ? "bg-forest text-cream border-forest"
                    : "bg-white border-forest/10 text-forest hover:border-forest/40"
                }`}
              >
                <span className={`font-mono text-[10px] ${active.id === l.id ? "text-amber" : "text-amber"}`}>
                  {l.n}
                </span>
                <span className="block mt-1 font-display font-bold text-lg leading-snug">{l.title}</span>
              </button>
            ))}
          </div>
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                data-testid="tech-layer-panel"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
                className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start"
              >
                <div className="relative overflow-hidden aspect-[4/3] border border-forest/10">
                  <img src={active.image} alt={active.alt} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber">Layer {active.n}</p>
                  <h3 className="mt-3 font-display font-extrabold text-2xl sm:text-3xl text-forest">{active.title}</h3>
                  <p className="mt-4 text-sm sm:text-base leading-relaxed text-moss">{active.desc}</p>
                  <ul className="mt-6 space-y-3 border-t border-forest/10 pt-6">
                    {active.specs.map((s) => (
                      <li key={s} className="flex items-start gap-3 text-sm text-moss">
                        <Check className="h-4 w-4 text-emerald shrink-0 mt-0.5" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-forest/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32">
          <Reveal><Tag>How we work</Tag></Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {APPROACH.map((a, i) => (
              <Reveal key={a.n} delay={i * 0.1}>
                <div data-testid={`approach-step-${a.n}`} className="border-t-2 border-emerald pt-6">
                  <span className="font-mono text-xs text-amber">{a.n}</span>
                  <h3 className="mt-3 font-display font-bold text-xl text-forest">{a.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-moss">{a.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
              <ParallaxImage src={IMG.labMicroscope} alt="Research work in the Fresh Guard laboratory" ratio="aspect-[16/9]" speed={8} testId="tech-image-lab" />
              <ParallaxImage src={IMG.greenhouse} alt="Greenhouse cultivation under controlled conditions" ratio="aspect-[16/9]" speed={12} testId="tech-image-greenhouse" />
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.2em] text-fog max-w-2xl leading-relaxed">
              Intellectual property — formulations and process know-how are managed as
              confidential R&D; formal IP protection is being evaluated as the programme matures.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 text-center">
        <Reveal>
          <h2 className="font-display font-extrabold tracking-tight leading-[1.1] text-3xl sm:text-4xl text-forest max-w-2xl mx-auto">
            Built for the businesses that feed the Kingdom.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            to="/for-business"
            data-testid="tech-cta-business"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-forest text-cream px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-emerald"
          >
            Fresh Guard for business
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
